// src/components/RawDatabasePage.js
//
// "Raw Database" — call-duty page for the coordinator. Displays everything a
// patient submitted through their referral link (the referral_submissions
// collection) as patient cards. Opening a card shows the full record in a
// modal where staff can EDIT the patient details, add diagnoses, set a call
// status, add timestamped notes, and review an append-only audit trail.
//
// Edits write back to the referral_submissions doc (staff-only per rules). The
// notes/audit live in subcollections. Nothing in the patient questionnaire /
// consent flow is affected.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { fetchSheetPatients } from "../services/sheetPatientsService";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getActiveSiteId, activeSiteUsesLegacySheet } from "../services/activeTenant";
import { useTenant } from "../context/TenantContext";
import { effectiveRole } from "../services/membershipService";
import {
  getReferralSubmissionsPath,
  getReferralSubmissionNotesPath,
  getReferralSubmissionAuditPath,
  getConsentsPath,
  getDuplicatesPath,
  getPatientNotesEntriesPath,
} from "../services/firestorePaths";
import { logPatientAuditEvent, getMrnLast4 } from "../services/patientAuditService";
import {
  findPotentialDuplicates,
  normalizeDob,
  sameMrnNumber,
  nameTokens,
} from "../services/patientDuplicateMatch";
import PotentialDuplicateDialog from "./PotentialDuplicateDialog";
import {
  DISEASE_OPTIONS,
  diagnosisLabel,
  surveyConditionLabel,
} from "../constants/diseaseOptions";
import {
  ArrowLeft,
  Search,
  Phone,
  Mail,
  Hash,
  MapPin,
  Calendar,
  CheckCircle2,
  X,
  Loader2,
  StickyNote,
  Save,
  Clock,
  User,
  History,
  FileText,
  Activity,
  Weight,
  HeartPulse,
  Pencil,
  Filter,
  Plus,
  Trash2,
  Stethoscope,
  ArrowRightCircle,
  Send,
  ChevronDown,
  AlertTriangle,
  ExternalLink,
} from "lucide-react";

// Tags reflect where a submission has been moved (or that it's still new).
// These replaced the old call-duty statuses (Called / No Answer / …).
const MOVE_TAGS = [
  { key: "new", label: "New", badge: "bg-slate-400/15 text-slate-200 border-slate-300/30" },
  { key: "referrals", label: "Moved to Referrals", badge: "bg-emerald-400/15 text-emerald-200 border-emerald-300/30" },
  { key: "duplicates", label: "Moved to Duplicates", badge: "bg-rose-400/15 text-rose-200 border-rose-300/30" },
];

const tagByKey = (key) => MOVE_TAGS.find((t) => t.key === key) || MOVE_TAGS[0];

// Derive a submission's tag from where it was moved.
const moveStatusOf = (s) =>
  s?.movedToReferrals ? tagByKey("referrals")
  : s?.movedToDuplicates ? tagByKey("duplicates")
  : tagByKey("new");

// Where the submission originated: the LungX LDCT bridge vs. a native RegX
// referral. LungX submissions carry `origin: "lungx-ldct"` (copied from the
// recipient by the stampReferralSubmissionMrn trigger); everything else = RegX.
// NOTE: uses `origin`, not `source` — the questionnaire sets `source` to
// "ReferralQuestionnaire" on every submission, so it can't distinguish origin.
const originOf = (s) =>
  String(s?.origin || "").toLowerCase().startsWith("lungx")
    ? { label: "LungX", badge: "bg-cyan-400/15 text-cyan-200 border-cyan-300/30" }
    : { label: "RegX", badge: "bg-indigo-400/15 text-indigo-200 border-indigo-300/30" };

const fmtTime = (ts) => {
  if (!ts) return "—";
  try {
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleString();
    return new Date(ts).toLocaleString();
  } catch {
    return "—";
  }
};

const millis = (ts) => {
  if (!ts) return 0;
  if (typeof ts.toMillis === "function") return ts.toMillis();
  if (ts.seconds) return ts.seconds * 1000;
  return 0;
};

// The questionnaire stores each condition as { name, exacerbations? ,
// glp1ForDiabetes?, usingCpap? }. Render that shape (with a string fallback).
const conditionLabel = (c) => {
  if (!c) return "Condition";
  if (typeof c === "string") return c;
  return c.name || c.condition || "Condition";
};

// Normalize an MRN to the 9-digit form the coordinator page requires (/^\d{9}$/):
// keep digits only, then left-pad with zeros to 9. Values already 9+ digits are
// returned digits-only unchanged (padStart is a no-op); empty stays empty. This
// preserves the "MRN must be exactly 9 digits" rule when a referral patient is
// moved into the coordinator flow.
const normalizeMrn9 = (raw) => {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits ? digits.padStart(9, "0") : "";
};

// Read-only dedup lookup used before "Move to Referrals" (single + bulk).
// Checks the active site's patient stores in priority order — consents >
// duplicates > legacy sheet (Dubois only) — and returns the single most
// relevant match, or null. Never writes to any collection.
async function findExistingPatientByMrn(rawMrn) {
  const normalized = normalizeMrn9(rawMrn);
  if (!normalized) return null;

  const consentsSnap = await getDocs(
    query(collection(db, getConsentsPath()), where("mrn", "==", normalized))
  );
  if (!consentsSnap.empty) {
    const d = consentsSnap.docs[0];
    return {
      querySrc: "firestore",
      label: "Consents (Lung Center Referrals)",
      id: d.id,
      ...d.data(),
    };
  }

  const dupSnap = await getDocs(
    query(collection(db, getDuplicatesPath()), where("mrn", "==", normalized))
  );
  if (!dupSnap.empty) {
    const d = dupSnap.docs[0];
    return { querySrc: "duplicates", label: "Duplicates", id: d.id, ...d.data() };
  }

  if (activeSiteUsesLegacySheet()) {
    try {
      const sheetPatients = await fetchSheetPatients();
      // Same MRN by the portal's one rule (leading zeros ignored).
      const match = sheetPatients.find((p) => sameMrnNumber(p.mrn, normalized));
      if (match) {
        return {
          querySrc: "sheet",
          label: "Legacy Google Sheet (Dubois)",
          id: match.token,
          ...match,
        };
      }
    } catch (e) {
      console.error("Sheet duplicate check failed:", e);
      // fail-open: a sheet-fetch error shouldn't block the move
    }
  }

  return null;
}

// Candidate list for the name + DOB dedup pass. Unlike findExistingPatientByMrn
// (an indexed equality query), fuzzy matching has to compare against every
// existing patient, so this pulls the active site's stores in full. Read-only,
// and fail-soft per store: a sheet outage narrows the check rather than
// blocking the move. Callers load it ONCE and reuse it across a bulk run.
async function loadDedupCandidates() {
  const candidates = [];

  const consentsSnap = await getDocs(collection(db, getConsentsPath()));
  consentsSnap.docs.forEach((d) =>
    candidates.push({ id: d.id, ...d.data(), sourceLabel: "Referrals" })
  );

  const dupSnap = await getDocs(collection(db, getDuplicatesPath()));
  dupSnap.docs.forEach((d) =>
    candidates.push({ id: d.id, ...d.data(), sourceLabel: "Duplicates" })
  );

  if (activeSiteUsesLegacySheet()) {
    try {
      const sheetPatients = await fetchSheetPatients();
      sheetPatients.forEach((p) =>
        candidates.push({
          id: p.token,
          ...p,
          name: p.fullName || p.name || "",
          sourceLabel: "Legacy Sheet",
        })
      );
    } catch (e) {
      console.error("Sheet fetch for name/DOB dedup failed:", e);
      // fail-open: a sheet outage shouldn't block the move
    }
  }

  return candidates;
}

const conditionDetail = (c) => {
  if (!c || typeof c === "string") return "";
  if (c.exacerbations != null && c.exacerbations !== "")
    return `Exacerbations last year: ${c.exacerbations}`;
  if (c.glp1ForDiabetes) return `On GLP-1: ${c.glp1ForDiabetes}`;
  if (c.usingCpap) return `Uses C-PAP: ${c.usingCpap}`;
  if (c.followup) return String(c.followup);
  return "";
};

const INPUT_CLS =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";
const SELECT_CLS = INPUT_CLS + " appearance-none cursor-pointer";
const LABEL_CLS =
  "block text-[10px] uppercase tracking-wider font-medium text-slate-400 mb-1";

// Diagnosis-specific clinical fields, keyed exactly as the consents collection
// stores them (PatientDetailPage writes these flat). The caller fills whatever
// applies during the call; on "Move to Referrals" they map 1:1 into consents.
const EMPTY_CLINICAL = {
  eosNA: false,
  eosValues: [],
  exacNA: false,
  exacerbations: [],
  ageNA: false,
  age: "",
  pftNA: false,
  pftDate: "",
  fev1fvcPre: "",
  fev1fvcPost: "",
  fev1Pre: "",
  fev1Post: "",
  fvcPre: "",
  fvcPost: "",
  revPercent: "",
  revMl: "",
  methNA: false,
  methResult: "",
  methDate: "",
  fenoNA: false,
  fenoPPB: "",
  fenoDate: "",
  oxygenNA: false,
  oxygenLitres: "",
  oxygenType: "PRN",
  cancerNA: false,
  cancerStatus: "",
  cancerDate: "",
  bmi: "",
  ahi: "",
  a1c: "",
  a1cDate: "",
  diabetes: "",
};

const initClinical = (s) => ({
  ...EMPTY_CLINICAL,
  // Pre-fill BMI from the weight-management answer when present.
  bmi: s && s.bmi != null && s.bmi !== "" ? String(s.bmi) : "",
});

// Build the "Legacy Notes" text from questionnaire answers that have NO
// structured field in consents (so nothing the patient reported is lost).
const buildLegacyNotes = (s) => {
  const lines = ["— Imported from Raw Database referral —"];
  if (s.weightManagementInterest && s.weightManagementInterest !== "not answered") {
    lines.push(`Weight management interest: ${s.weightManagementInterest}`);
    if (s.height) lines.push(`Height: ${s.height}`);
    if (s.weightLbs != null && s.weightLbs !== "") lines.push(`Weight: ${s.weightLbs} lbs`);
    if (s.bmi != null && s.bmi !== "") lines.push(`BMI (reported): ${s.bmi}`);
    if (s.onGlp1WeightMgmt) lines.push(`On GLP-1 (weight mgmt): ${s.onGlp1WeightMgmt}`);
  }
  const conds = Array.isArray(s.conditions) ? s.conditions : [];
  if (conds.length) {
    lines.push("Reported conditions:");
    conds.forEach((c) => {
      const d = conditionDetail(c);
      lines.push(`  • ${conditionLabel(c)}${d ? ` (${d})` : ""}`);
    });
  }
  if (s.printedName) lines.push(`E-sign printed name: ${s.printedName}`);
  return lines.join("\n");
};

/** The first non-empty line of a note, for the one-line card preview. Empty
 *  string for a missing/blank note, so a card can test it directly. */
const firstLineOf = (body) =>
  String(body || "")
    .split("\n")
    .map((l) => l.trim())
    .find(Boolean) || "";

const PageBackground = () => (
  <div className="fixed inset-0 -z-10 overflow-hidden">
    <img
      src="/bg_ai.jpg"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.25),_transparent_55%)]" />
    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,_rgba(37,99,235,0.22),_transparent_55%)]" />
  </div>
);

// Collapsible clinical section (mirrors PatientDetailPage's collapsible blocks).
const ClinicalSection = ({ title, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="rounded-xl border border-white/10 bg-white/[0.025] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-white/[0.02] transition"
        aria-expanded={open}
      >
        <Stethoscope className="w-4 h-4 text-purple-300" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
          {title}
        </span>
        <ChevronDown
          className={`ml-auto w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-3 pb-3 space-y-3">{children}</div>}
    </section>
  );
};

// ---------------------------------------------------------------------------
// Detail / edit modal
// ---------------------------------------------------------------------------
const SubmissionModal = ({ submission, onClose, onLatestNote }) => {
  const id = submission.id;
  const [notes, setNotes] = useState([]);
  const [audit, setAudit] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  // Clinical data the caller fills before moving the patient to Referrals.
  const [clinical, setClinical] = useState(() => initClinical(submission));
  const [moving, setMoving] = useState(false);
  const [movingDup, setMovingDup] = useState(false);
  const [checkingDup, setCheckingDup] = useState(false);
  const [dupMatch, setDupMatch] = useState(null);
  // Name + DOB matches found after the MRN check came back clean. Non-empty
  // means the advisory dialog is up; the move itself is unchanged.
  const [nameDobMatches, setNameDobMatches] = useState([]);
  // MRN is read-only for everyone EXCEPT admins/owners — same rule and same
  // membership-derived role source as PatientDetailPage.js's canEditMrn.
  const { access, tenantId: activeTenantId, siteId: activeSiteId, membershipLoading } =
    useTenant();
  const resolvedRole = effectiveRole(access, activeTenantId, activeSiteId);
  const normalizedRole = (resolvedRole || "").toLowerCase();
  const canEditMrn =
    !membershipLoading &&
    (normalizedRole === "admin" || normalizedRole === "owner" || Boolean(access?.isOwner));
  const alreadyMoved = !!submission.movedToReferrals;
  const alreadyMovedToDuplicates = !!submission.movedToDuplicates;
  const dx = Array.isArray(submission.diagnosis) ? submission.diagnosis : [];
  const showRespiratory =
    dx.includes("Asthma") || dx.includes("COPD") || dx.includes("Bronchiectasis");
  const showSleep = dx.includes("Sleep Apnea");
  const setC = (k, v) => setClinical((c) => ({ ...c, [k]: v }));

  // Live notes (newest first).
  useEffect(() => {
    const q = query(
      collection(db, getReferralSubmissionNotesPath(id)),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(rows);
        // Hand the newest note back to the list, so the card's preview updates
        // the moment one is added here rather than on the next page load. This
        // subscription is already open — the list would otherwise have to
        // re-read the same subcollection to learn what it can see right here.
        onLatestNote?.(id, rows[0] || null);
      },
      (err) => console.error("Referral notes subscribe failed:", err)
    );
    return () => unsub();
    // onLatestNote is a stable useCallback on the page; listed for correctness.
  }, [id, onLatestNote]);

  // Live audit trail (newest first).
  useEffect(() => {
    const q = query(
      collection(db, getReferralSubmissionAuditPath(id)),
      orderBy("at", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setAudit(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error("Referral audit subscribe failed:", err)
    );
    return () => unsub();
  }, [id]);

  const writeAudit = async (eventType, detail) => {
    try {
      await addDoc(collection(db, getReferralSubmissionAuditPath(id)), {
        type: eventType,
        detail: detail || "",
        actor: auth.currentUser?.email || "unknown",
        at: serverTimestamp(),
      });
    } catch (err) {
      console.error("Referral audit write failed:", err);
    }
  };

  const startEdit = () => {
    setForm({
      name: submission.name || "",
      dob: submission.dob || "",
      phone: submission.phone || "",
      email: submission.email || "",
      address: submission.address || "",
      mrn: submission.mrn || "",
      weightManagementInterest: submission.weightManagementInterest || "not answered",
      height: submission.height || "",
      weightLbs: submission.weightLbs != null ? String(submission.weightLbs) : "",
      bmi: submission.bmi != null ? String(submission.bmi) : "",
      onGlp1WeightMgmt: submission.onGlp1WeightMgmt || "not answered",
      diagnosis: Array.isArray(submission.diagnosis) ? [...submission.diagnosis] : [],
    });
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setForm(null);
  };

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleDiagnosis = (d) =>
    setForm((f) => ({
      ...f,
      diagnosis: f.diagnosis.includes(d)
        ? f.diagnosis.filter((x) => x !== d)
        : [...f.diagnosis, d],
    }));

  const saveEdit = async () => {
    if (!form || savingEdit) return;
    setSavingEdit(true);
    try {
      const w = parseFloat(form.weightLbs);
      const b = parseFloat(form.bmi);
      await updateDoc(doc(db, getReferralSubmissionsPath(), id), {
        name: form.name.trim(),
        dob: form.dob,
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        // MRN is written back only when the current user is allowed to edit it
        // (admin/owner) — same guard as canEditMrn in PatientDetailPage.js.
        ...(canEditMrn ? { mrn: normalizeMrn9(form.mrn) } : {}),
        weightManagementInterest: form.weightManagementInterest,
        height: form.height,
        weightLbs: isNaN(w) ? null : w,
        bmi: isNaN(b) ? null : b,
        onGlp1WeightMgmt: form.onGlp1WeightMgmt,
        diagnosis: form.diagnosis,
        lastEditedBy: auth.currentUser?.email || "unknown",
        lastEditedAt: serverTimestamp(),
      });
      await writeAudit("edit", "Patient details updated");
      setEditing(false);
      setForm(null);
    } catch (err) {
      console.error("Save edit failed:", err);
    } finally {
      setSavingEdit(false);
    }
  };

  const handleAddNote = async () => {
    const body = noteDraft.trim();
    if (!body || savingNote) return;
    setSavingNote(true);
    try {
      await addDoc(collection(db, getReferralSubmissionNotesPath(id)), {
        body,
        createdByEmail: auth.currentUser?.email || "unknown",
        createdAt: serverTimestamp(),
      });
      // Denormalized onto the parent doc so the "Recently noted" sort on the
      // list page can read it straight off the initial snapshot — no
      // per-submission query needed (see RawDatabasePage's sortSubmissions).
      await updateDoc(doc(db, getReferralSubmissionsPath(), id), {
        lastNoteAt: serverTimestamp(),
      });
      await writeAudit("note", `Note added: ${body.slice(0, 80)}`);
      setNoteDraft("");
    } catch (err) {
      console.error("Add referral note failed:", err);
    } finally {
      setSavingNote(false);
    }
  };

  // Entry point for the "Move to Referrals" button. Runs a read-only MRN
  // dedup check first; if a match exists, shows the duplicate-warning
  // dialog instead of moving immediately. Otherwise behaves exactly as
  // before (single confirm, then the move).
  const handleMoveToReferrals = async () => {
    if (alreadyMoved || moving || checkingDup) return;

    setCheckingDup(true);
    let match = null;
    try {
      match = await findExistingPatientByMrn(submission.mrn);
    } catch (e) {
      console.error("Duplicate MRN check failed:", e);
      // fail-open: don't block the move over a failed check
    }
    setCheckingDup(false);

    if (match) {
      setDupMatch(match);
      return;
    }

    // Second pass: the MRN is new, but is this the same PERSON already in the
    // portal under a mistyped MRN? Exact DOB plus a fuzzy name match. Advisory
    // It opens a dialog asking staff to recheck the MRN, not a refusal.
    setCheckingDup(true);
    let nameDobHits = [];
    try {
      // Skip the full-collection read entirely when there's nothing to match on
      // findPotentialDuplicates needs a readable DOB and at least one name
      // token (a partial name still matches, at a lower score).
      if (normalizeDob(submission.dob) && nameTokens(submission.name).length >= 1) {
        nameDobHits = findPotentialDuplicates(
          { name: submission.name, dob: submission.dob, mrn: submission.mrn },
          await loadDedupCandidates()
        );
      }
    } catch (e) {
      console.error("Name/DOB duplicate check failed:", e);
      // fail-open: don't block the move over a failed check
    }
    setCheckingDup(false);

    if (nameDobHits.length) {
      setNameDobMatches(nameDobHits);
      return;
    }

    if (
      !window.confirm(
        "Copy this patient into Lung Center Referrals?\n\nThis creates a patient record in the consents collection with the details and clinical data entered here. The Raw Database entry is NOT removed — it moves to 'Recently Sent Patients'."
      )
    )
      return;

    await performMoveToReferrals();
  };

  // Does the actual copy into consents — no confirm dialogs of its own.
  // Called either after the plain confirm above (no duplicate found) or
  // after the duplicate-warning dialog's "Proceed Anyway" path.
  const performMoveToReferrals = async () => {
    setMoving(true);
    try {
      const actor = auth.currentUser?.email || "unknown";
      const dxArr = Array.isArray(submission.diagnosis) ? submission.diagnosis : [];

      // Patient record mirroring PatientDetailPage's consents shape: mapped
      // values filled, everything else left empty for later completion.
      const payload = {
        // profile (mapped from the questionnaire submission)
        name: submission.name || "",
        dob: submission.dob || "",
        phone: submission.phone || "",
        email: submission.email || "",
        physicalAddress: submission.address || "",
        diagnosis: dxArr,
        status: "Lung Center Referrals",
        notes: buildLegacyNotes(submission),
        consentUrl: submission.consentPdfUrl || "",
        // MRN carried through from the referral submission (mapped from the
        // mass-text pipeline), normalized to 9 digits so it satisfies the
        // coordinator page's "exactly 9 digits" rule. Blank if there was none.
        mrn: normalizeMrn9(submission.mrn),
        study: "",
        potentialStudyIds: [],
        studyEligibility: {},
        emergencyContactName: "",
        emergencyContactPhone: "",
        callMade: false,
        scheduled: false,
        dateScheduled: "",
        rescreenDate: "",
        urgent: false,
        urgentReason: "",
        // clinical (flat — same keys the consents collection uses)
        ...clinical,
        // provenance
        source: "ReferralRawDatabase",
        referralSubmissionId: id,
        referralToken: submission.token || "",
        signedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        movedToReferralsBy: actor,
        movedToReferralsAt: serverTimestamp(),
      };

      const consRef = await addDoc(collection(db, getConsentsPath()), payload);
      const newId = consRef.id;

      // Copy coordinator notes -> patient_notes/{newId}/notes (preserve time).
      for (const n of notes) {
        try {
          await addDoc(collection(db, getPatientNotesEntriesPath(newId)), {
            body: n.body || "",
            patientId: newId,
            patientName: submission.name || "",
            mrnLast4: getMrnLast4(normalizeMrn9(submission.mrn)),
            source: "ReferralRawDatabase",
            createdByEmail: n.createdByEmail || "unknown",
            createdByUid: null,
            createdByRole: "",
            createdAt: n.createdAt || serverTimestamp(),
            schemaVersion: 1,
          });
        } catch (e) {
          console.error("Note copy failed:", e);
        }
      }

      // Copy audit trail -> patient_audits/{newId}/events (oldest first).
      await logPatientAuditEvent({
        patientId: newId,
        title: `Referral form submitted via ${submission.source || "referral link"}`,
        action: "REFERRAL_FORM_SUBMITTED",
        patientName: submission.name || "",
        actorEmail: "patient",
        metadata: {
          submittedAt: fmtTime(submission.submittedAt),
          ipAddress: submission.ipAddress || "",
        },
      });
      for (const ev of [...audit].reverse()) {
        await logPatientAuditEvent({
          patientId: newId,
          title: ev.detail || ev.type || "Referral event",
          action: "REFERRAL_" + String(ev.type || "event").toUpperCase(),
          patientName: submission.name || "",
          actorEmail: ev.actor || "unknown",
          metadata: { copiedFromReferral: true, originalAt: fmtTime(ev.at) },
        });
      }
      await logPatientAuditEvent({
        patientId: newId,
        title: "Moved to Lung Center Referrals from Raw Database",
        action: "REFERRAL_MOVED_TO_CONSENTS",
        patientName: submission.name || "",
        actorEmail: actor,
        metadata: { referralSubmissionId: id },
      });

      // Flag the referral submission (keep it — do NOT delete).
      await updateDoc(doc(db, getReferralSubmissionsPath(), id), {
        movedToReferrals: true,
        movedToReferralsAt: serverTimestamp(),
        movedToReferralsBy: actor,
        consentDocId: newId,
      });
      await writeAudit("move", `Moved to Lung Center Referrals (consents/${newId})`);

      onClose();
    } catch (err) {
      console.error("Move to Referrals failed:", err);
      window.alert("Failed to move to Referrals: " + (err?.message || "unknown error"));
    } finally {
      setMoving(false);
    }
  };

  // COPY (not move) this submission into the duplicates collection as a
  // "Potential Duplicate" patient — the same mechanism as Move to Referrals,
  // but targeting the coordinator dashboard's Duplicates section. The Raw
  // Database record stays put (flagged movedToDuplicates).
  const handleMoveToDuplicates = async () => {
    if (alreadyMovedToDuplicates || movingDup) return;
    if (
      !window.confirm(
        "Copy this patient into the Duplicates section?\n\nThis creates a record in the duplicates collection with the details and clinical data entered here. The Raw Database entry is NOT removed."
      )
    )
      return;

    setMovingDup(true);
    try {
      const actor = auth.currentUser?.email || "unknown";
      const dxArr = Array.isArray(submission.diagnosis) ? submission.diagnosis : [];

      const payload = {
        name: submission.name || "",
        dob: submission.dob || "",
        phone: submission.phone || "",
        email: submission.email || "",
        physicalAddress: submission.address || "",
        diagnosis: dxArr,
        status: "Potential Duplicate",
        notes: buildLegacyNotes(submission),
        consentUrl: submission.consentPdfUrl || "",
        mrn: normalizeMrn9(submission.mrn),
        study: "",
        potentialStudyIds: [],
        studyEligibility: {},
        emergencyContactName: "",
        emergencyContactPhone: "",
        callMade: false,
        scheduled: false,
        dateScheduled: "",
        rescreenDate: "",
        urgent: false,
        urgentReason: "",
        ...clinical,
        source: "ReferralRawDatabase",
        referralSubmissionId: id,
        referralToken: submission.token || "",
        signedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        movedToDuplicatesBy: actor,
        movedToDuplicatesAt: serverTimestamp(),
      };

      const dupRef = await addDoc(collection(db, getDuplicatesPath()), payload);
      const newId = dupRef.id;

      // Copy coordinator notes -> patient_notes/{newId}/notes (preserve time).
      for (const n of notes) {
        try {
          await addDoc(collection(db, getPatientNotesEntriesPath(newId)), {
            body: n.body || "",
            patientId: newId,
            patientName: submission.name || "",
            mrnLast4: getMrnLast4(normalizeMrn9(submission.mrn)),
            source: "ReferralRawDatabase",
            createdByEmail: n.createdByEmail || "unknown",
            createdByUid: null,
            createdByRole: "",
            createdAt: n.createdAt || serverTimestamp(),
            schemaVersion: 1,
          });
        } catch (e) {
          console.error("Note copy failed:", e);
        }
      }

      await logPatientAuditEvent({
        patientId: newId,
        title: "Moved to Duplicates from Raw Database",
        action: "REFERRAL_MOVED_TO_DUPLICATES",
        patientName: submission.name || "",
        actorEmail: actor,
        metadata: { referralSubmissionId: id },
      });

      // Flag the referral submission (keep it — do NOT delete).
      await updateDoc(doc(db, getReferralSubmissionsPath(), id), {
        movedToDuplicates: true,
        movedToDuplicatesAt: serverTimestamp(),
        movedToDuplicatesBy: actor,
        duplicateDocId: newId,
      });
      await writeAudit("move", `Moved to Duplicates (duplicates/${newId})`);

      onClose();
    } catch (err) {
      console.error("Move to Duplicates failed:", err);
      window.alert("Failed to move to Duplicates: " + (err?.message || "unknown error"));
    } finally {
      setMovingDup(false);
    }
  };

  const conditions = Array.isArray(submission.conditions)
    ? submission.conditions
    : [];
  const diagnosis = Array.isArray(submission.diagnosis)
    ? submission.diagnosis
    : [];
  const current = moveStatusOf(submission);
  const showWeight =
    submission.weightManagementInterest &&
    submission.weightManagementInterest !== "not answered";

  // Read-only labelled value.
  const Field = ({ icon: Icon, label, value, href }) =>
    value ? (
      <div className="flex items-start gap-2">
        {Icon && <Icon className="w-4 h-4 text-purple-300 mt-0.5 shrink-0" />}
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">
            {label}
          </div>
          {href ? (
            <a href={href} className="text-sm text-sky-300 hover:underline break-words">
              {value}
            </a>
          ) : (
            <div className="text-sm text-slate-100 break-words">{value}</div>
          )}
        </div>
      </div>
    ) : null;

  // ---- Clinical field renderers ----------------------------------------
  // Each maps 1:1 to a consents field. They are reused across the per-diagnosis
  // sections below, exactly like PatientDetailPage (which renders a separate
  // section per diagnosis bound to the same flat fields).
  const naHeader = (label, naKey) => (
    <div className="flex items-center justify-between mb-1">
      <span className={LABEL_CLS + " mb-0"}>{label}</span>
      <label className="flex items-center gap-1 text-[10px] text-slate-400">
        <input type="checkbox" checked={clinical[naKey]} onChange={(e) => setC(naKey, e.target.checked)} /> N/A
      </label>
    </div>
  );

  const renderEos = () => (
    <div>
      {naHeader("EOS", "eosNA")}
      {!clinical.eosNA && (
        <div className="space-y-1.5">
          {clinical.eosValues.map((e, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <input type="number" placeholder="EOS" className={INPUT_CLS} value={e.value}
                onChange={(ev) => { const arr = [...clinical.eosValues]; arr[i] = { ...arr[i], value: ev.target.value }; setC("eosValues", arr); }} />
              <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={e.date}
                onChange={(ev) => { const arr = [...clinical.eosValues]; arr[i] = { ...arr[i], date: ev.target.value }; setC("eosValues", arr); }} />
              <button onClick={() => setC("eosValues", clinical.eosValues.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setC("eosValues", [...clinical.eosValues, { value: "", date: "" }])} className="inline-flex items-center gap-1 text-xs text-purple-300 hover:text-white">
            <Plus className="w-3 h-3" /> Add EOS
          </button>
        </div>
      )}
    </div>
  );

  const renderExac = () => (
    <div>
      {naHeader("Exacerbations (last year)", "exacNA")}
      {!clinical.exacNA && (
        <div className="space-y-1.5">
          {clinical.exacerbations.map((e, i) => (
            <div key={i} className="flex gap-1.5 items-center">
              <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={e.date}
                onChange={(ev) => { const arr = [...clinical.exacerbations]; arr[i] = { ...arr[i], date: ev.target.value }; setC("exacerbations", arr); }} />
              <select className={SELECT_CLS} value={e.type}
                onChange={(ev) => { const arr = [...clinical.exacerbations]; arr[i] = { ...arr[i], type: ev.target.value }; setC("exacerbations", arr); }}>
                <option>Outpatient</option>
                <option>Hospital</option>
              </select>
              <button onClick={() => setC("exacerbations", clinical.exacerbations.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-rose-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={() => setC("exacerbations", [...clinical.exacerbations, { date: "", type: "Outpatient" }])} className="inline-flex items-center gap-1 text-xs text-purple-300 hover:text-white">
            <Plus className="w-3 h-3" /> Add Exacerbation
          </button>
        </div>
      )}
    </div>
  );

  const renderAge = (withNa = true) =>
    withNa ? (
      <div>
        {naHeader("Age", "ageNA")}
        {!clinical.ageNA && (
          <input type="number" className={INPUT_CLS} value={clinical.age} onChange={(e) => setC("age", e.target.value)} />
        )}
      </div>
    ) : (
      <div>
        <span className={LABEL_CLS}>Age</span>
        <input type="number" className={INPUT_CLS} value={clinical.age} onChange={(e) => setC("age", e.target.value)} />
      </div>
    );

  const renderPft = () => (
    <div>
      {naHeader("PFT", "pftNA")}
      {!clinical.pftNA && (
        <div className="space-y-2">
          <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={clinical.pftDate} onChange={(e) => setC("pftDate", e.target.value)} />
          <div className="grid grid-cols-2 gap-2">
            <input type="number" placeholder="FEV1/FVC Pre" className={INPUT_CLS} value={clinical.fev1fvcPre} onChange={(e) => setC("fev1fvcPre", e.target.value)} />
            <input type="number" placeholder="FEV1/FVC Post" className={INPUT_CLS} value={clinical.fev1fvcPost} onChange={(e) => setC("fev1fvcPost", e.target.value)} />
            <input type="number" placeholder="FEV1 % Pre" className={INPUT_CLS} value={clinical.fev1Pre} onChange={(e) => setC("fev1Pre", e.target.value)} />
            <input type="number" placeholder="FEV1 % Post" className={INPUT_CLS} value={clinical.fev1Post} onChange={(e) => setC("fev1Post", e.target.value)} />
            <input type="number" placeholder="FVC % Pre" className={INPUT_CLS} value={clinical.fvcPre} onChange={(e) => setC("fvcPre", e.target.value)} />
            <input type="number" placeholder="FVC % Post" className={INPUT_CLS} value={clinical.fvcPost} onChange={(e) => setC("fvcPost", e.target.value)} />
            <input type="number" placeholder="Reversibility % change" className={INPUT_CLS} value={clinical.revPercent} onChange={(e) => setC("revPercent", e.target.value)} />
            <input type="number" placeholder="Reversibility ml" className={INPUT_CLS} value={clinical.revMl} onChange={(e) => setC("revMl", e.target.value)} />
          </div>
        </div>
      )}
    </div>
  );

  const renderFeno = () => (
    <div>
      {naHeader("FENO", "fenoNA")}
      {!clinical.fenoNA && (
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="PPb" className={INPUT_CLS} value={clinical.fenoPPB} onChange={(e) => setC("fenoPPB", e.target.value)} />
          <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={clinical.fenoDate} onChange={(e) => setC("fenoDate", e.target.value)} />
        </div>
      )}
    </div>
  );

  const renderMeth = () => (
    <div>
      {naHeader("Methacholine Challenge", "methNA")}
      {!clinical.methNA && (
        <div className="grid grid-cols-2 gap-2">
          <select className={SELECT_CLS} value={clinical.methResult} onChange={(e) => setC("methResult", e.target.value)}>
            <option value="">Result</option>
            <option>Positive</option>
            <option>Negative</option>
          </select>
          <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={clinical.methDate} onChange={(e) => setC("methDate", e.target.value)} />
        </div>
      )}
    </div>
  );

  const renderOxygen = () => (
    <div>
      {naHeader("Oxygen", "oxygenNA")}
      {!clinical.oxygenNA && (
        <div className="grid grid-cols-2 gap-2">
          <input type="number" placeholder="Litres" className={INPUT_CLS} value={clinical.oxygenLitres} onChange={(e) => setC("oxygenLitres", e.target.value)} />
          <select className={SELECT_CLS} value={clinical.oxygenType} onChange={(e) => setC("oxygenType", e.target.value)}>
            <option>PRN</option>
            <option>Night</option>
            <option>Continuous</option>
          </select>
        </div>
      )}
    </div>
  );

  const renderCancer = () => (
    <div>
      {naHeader("Cancer (except Basal/Squamous skin, last 5 yrs)", "cancerNA")}
      {!clinical.cancerNA && (
        <div className="grid grid-cols-2 gap-2">
          <select className={SELECT_CLS} value={clinical.cancerStatus} onChange={(e) => setC("cancerStatus", e.target.value)}>
            <option value="">Select</option>
            <option>Yes</option>
            <option>No</option>
          </select>
          <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={clinical.cancerDate} onChange={(e) => setC("cancerDate", e.target.value)} />
        </div>
      )}
    </div>
  );

  const moveButton = (compact = false) => (
    <button
      onClick={handleMoveToReferrals}
      disabled={moving || checkingDup}
      className={
        compact
          ? "inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] transition disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
          : "inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
      }
    >
      {moving || checkingDup ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <ArrowRightCircle className="w-4 h-4" />
      )}
      {moving ? "Moving…" : checkingDup ? "Checking…" : "Move to Referrals"}
    </button>
  );

  // Warning dialog shown when handleMoveToReferrals finds an existing patient
  // with the same MRN. Layered above the SubmissionModal itself.
  const duplicateWarningDialog = () => {
    if (!dupMatch) return null;
    const displayName = dupMatch.name || dupMatch.fullName || "Unknown name";
    const displayDob = dupMatch.dob || "—";
    const displayStatus = dupMatch.status || "—";
    const displayStudy = dupMatch.study || "";

    const viewExisting = () => {
      window.open(
        `/coordinator/patient/${dupMatch.id}?src=${dupMatch.querySrc}`,
        "_blank",
        "noopener,noreferrer"
      );
    };

    const proceedAnyway = () => {
      if (
        window.confirm(
          "Are you sure you want to move this patient to Referrals anyway? A possible duplicate was found."
        )
      ) {
        setDupMatch(null);
        performMoveToReferrals();
      }
    };

    return (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
        onClick={() => setDupMatch(null)}
      >
        <div
          className="relative w-full max-w-md rounded-2xl border border-rose-400/30 bg-slate-950/80 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] p-5"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-10 h-10 rounded-full bg-rose-500/15 border border-rose-400/30 flex items-center justify-center">
              <AlertTriangle className="w-5 h-5 text-rose-300" />
            </div>
            <div className="min-w-0">
              <h3 className="text-base font-bold text-white">Possible Duplicate Patient</h3>
              <p className="mt-1 text-sm text-slate-300">
                A patient with this MRN already exists in{" "}
                <span className="font-semibold text-rose-200">{dupMatch.label}</span>.
              </p>
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-white/10 bg-white/[0.03] p-3 space-y-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-slate-400">Name</span>
              <span className="text-slate-100 font-medium truncate">{displayName}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-slate-400">DOB</span>
              <span className="text-slate-100 font-medium">{displayDob}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-slate-400">Status</span>
              <span className="text-slate-100 font-medium truncate">{displayStatus}</span>
            </div>
            {displayStudy && (
              <div className="flex justify-between gap-3">
                <span className="text-slate-400">Study</span>
                <span className="text-slate-100 font-medium truncate">{displayStudy}</span>
              </div>
            )}
          </div>

          <button
            onClick={viewExisting}
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm font-medium text-slate-200 hover:bg-white/10 transition"
          >
            <ExternalLink className="w-3.5 h-3.5" />
            View existing record
          </button>

          <div className="mt-4 flex items-center gap-2">
            <button
              onClick={() => setDupMatch(null)}
              autoFocus
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] transition"
            >
              Cancel
            </button>
            <button
              onClick={proceedAnyway}
              className="px-3 py-2.5 rounded-xl bg-rose-500/15 border border-rose-400/40 text-xs font-medium text-rose-300 hover:bg-rose-500/25 transition"
            >
              Proceed Anyway
            </button>
          </div>
        </div>
      </div>
    );
  };

  const duplicatesButton = () => (
    <button
      onClick={handleMoveToDuplicates}
      disabled={movingDup}
      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-rose-500/15 border border-rose-400/30 text-sm font-semibold text-rose-200 hover:bg-rose-500/25 transition disabled:opacity-60 disabled:cursor-not-allowed shrink-0"
    >
      {movingDup ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightCircle className="w-4 h-4" />}
      {movingDup ? "Moving…" : "Move to Duplicates"}
    </button>
  );

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-2xl my-8 rounded-2xl border border-white/10 bg-slate-950/80 backdrop-blur-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70 rounded-t-2xl" />

        {/* Header */}
        <div className="flex items-start justify-between gap-3 p-5 border-b border-white/10">
          <div className="min-w-0 flex-1">
            {editing ? (
              <input
                className={INPUT_CLS + " text-lg font-semibold"}
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="Patient name"
              />
            ) : (
              <h2 className="text-xl font-bold text-white truncate">
                {submission.name || "Unknown patient"}
              </h2>
            )}
            <div className="mt-1 flex items-center gap-2 flex-wrap">
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${current.badge}`}>
                {current.label}
              </span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full border ${originOf(submission).badge}`}>
                {originOf(submission).label}
              </span>
              <span className="text-xs text-slate-400">
                Submitted {fmtTime(submission.submittedAt)}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {editing ? (
              <>
                <button
                  onClick={saveEdit}
                  disabled={savingEdit}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-sm font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] transition disabled:opacity-60"
                >
                  {savingEdit ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {savingEdit ? "Saving…" : "Save"}
                </button>
                <button
                  onClick={cancelEdit}
                  disabled={savingEdit}
                  className="px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-300 hover:bg-white/10 transition"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={startEdit}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition"
              >
                <Pencil className="w-4 h-4 text-purple-300" />
                Edit
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/[0.05] border border-white/10 text-slate-300 hover:bg-white/10 transition"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Always-visible action bar (outside the scroll area) */}
        {!editing && (
          <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between gap-3 bg-white/[0.02] flex-wrap">
            {alreadyMoved || alreadyMovedToDuplicates ? (
              <span className="inline-flex items-center gap-2 text-sm flex-wrap">
                {alreadyMoved && (
                  <span className="inline-flex items-center gap-1.5 text-emerald-200">
                    <CheckCircle2 className="w-4 h-4" /> Moved to Lung Center Referrals
                  </span>
                )}
                {alreadyMovedToDuplicates && (
                  <span className="inline-flex items-center gap-1.5 text-rose-200">
                    <CheckCircle2 className="w-4 h-4" /> Moved to Duplicates
                  </span>
                )}
              </span>
            ) : (
              <>
                <span className="text-xs text-slate-400">
                  Fill clinical data below, then move this patient to Referrals or Duplicates.
                </span>
                <div className="flex items-center gap-2 flex-wrap">
                  {duplicatesButton()}
                  {moveButton(true)}
                </div>
              </>
            )}
          </div>
        )}

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Contact + personal */}
          <section className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-purple-300 font-semibold mb-3">
              Patient Details
            </div>
            {editing ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={LABEL_CLS}>Date of Birth</label>
                  <input type="date" className={INPUT_CLS} value={form.dob} onChange={(e) => setField("dob", e.target.value)} />
                </div>
                <div>
                  <label className={LABEL_CLS}>Phone</label>
                  <input className={INPUT_CLS} value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+1…" />
                </div>
                <div>
                  <label className={LABEL_CLS}>Email</label>
                  <input className={INPUT_CLS} value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="name@example.com" />
                </div>
                <div>
                  <label className={LABEL_CLS}>Address</label>
                  <input className={INPUT_CLS} value={form.address} onChange={(e) => setField("address", e.target.value)} placeholder="Address" />
                </div>
                <div>
                  <label className={LABEL_CLS}>MRN{!canEditMrn && " (admin only)"}</label>
                  <input
                    className={INPUT_CLS}
                    value={form.mrn}
                    readOnly={!canEditMrn}
                    onChange={canEditMrn ? (e) => setField("mrn", e.target.value) : undefined}
                    placeholder="9-digit MRN"
                  />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field icon={Phone} label="Phone" value={submission.phone} href={submission.phone ? `tel:${submission.phone}` : null} />
                <Field icon={Mail} label="Email" value={submission.email} href={submission.email ? `mailto:${submission.email}` : null} />
                <Field icon={Calendar} label="Date of Birth" value={submission.dob} />
                <Field icon={MapPin} label="Address" value={submission.address} />
                {/* MRN is mapped server-side from the mass-text pipeline (never
                    shown to the patient). Read-only, normalized to 9 digits. */}
                <Field icon={Hash} label="MRN" value={normalizeMrn9(submission.mrn)} />
              </div>
            )}
          </section>

          {/* Diagnosis (editable pills — same options as PatientDetailPage) */}
          <section className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
            <div className="flex items-center gap-2 mb-2">
              <HeartPulse className="w-4 h-4 text-purple-300" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
                Diagnosis
              </span>
            </div>
            {editing ? (
              <div className="flex flex-wrap gap-1.5">
                {DISEASE_OPTIONS.map((d) => {
                  const active = form.diagnosis.includes(d);
                  return (
                    <button
                      type="button"
                      key={d}
                      onClick={() => toggleDiagnosis(d)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                        active
                          ? "bg-gradient-to-r from-purple-500/30 to-blue-500/30 border-purple-300/40 text-white"
                          : "bg-white/[0.04] border-white/10 text-slate-300 hover:bg-white/[0.08] hover:text-white"
                      }`}
                    >
                      {active ? (
                        <CheckCircle2 className="w-3 h-3 text-emerald-300" />
                      ) : (
                        <span className="w-3 h-3 rounded-full border border-white/20" />
                      )}
                      {diagnosisLabel(d)}
                    </button>
                  );
                })}
              </div>
            ) : diagnosis.length === 0 ? (
              <span className="text-xs text-slate-500 italic">None selected — click Edit to add.</span>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {diagnosis.map((d) => (
                  <span
                    key={d}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/15 border border-purple-300/30 text-purple-200"
                  >
                    <HeartPulse className="w-3 h-3" />
                    {diagnosisLabel(d)}
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Weight management */}
          {(editing || showWeight) && (
            <section className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
              <div className="flex items-center gap-2 mb-2">
                <Weight className="w-4 h-4 text-purple-300" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
                  Weight Management
                </span>
              </div>
              {editing ? (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLS}>Interested</label>
                    <select className={INPUT_CLS} value={form.weightManagementInterest} onChange={(e) => setField("weightManagementInterest", e.target.value)}>
                      <option value="yes">yes</option>
                      <option value="no">no</option>
                      <option value="not answered">not answered</option>
                    </select>
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Height</label>
                    <input className={INPUT_CLS} value={form.height} onChange={(e) => setField("height", e.target.value)} placeholder={`e.g. 5'9"`} />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>Weight (lbs)</label>
                    <input className={INPUT_CLS} inputMode="decimal" value={form.weightLbs} onChange={(e) => setField("weightLbs", e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>BMI</label>
                    <input className={INPUT_CLS} inputMode="decimal" value={form.bmi} onChange={(e) => setField("bmi", e.target.value)} />
                  </div>
                  <div>
                    <label className={LABEL_CLS}>On GLP-1</label>
                    <select className={INPUT_CLS} value={form.onGlp1WeightMgmt} onChange={(e) => setField("onGlp1WeightMgmt", e.target.value)}>
                      <option value="yes">yes</option>
                      <option value="no">no</option>
                      <option value="not answered">not answered</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Interested</div>
                    <div className="text-slate-100 capitalize">{submission.weightManagementInterest}</div>
                  </div>
                  {submission.height && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Height</div>
                      <div className="text-slate-100">{submission.height}</div>
                    </div>
                  )}
                  {submission.weightLbs != null && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Weight</div>
                      <div className="text-slate-100">{submission.weightLbs} lbs</div>
                    </div>
                  )}
                  {submission.bmi != null && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">BMI</div>
                      <div className="text-slate-100">{submission.bmi}</div>
                    </div>
                  )}
                  {submission.onGlp1WeightMgmt && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">On GLP-1</div>
                      <div className="text-slate-100 capitalize">{submission.onGlp1WeightMgmt}</div>
                    </div>
                  )}
                </div>
              )}
            </section>
          )}

          {/* Conditions (from the questionnaire — read-only) */}
          {conditions.length > 0 && (
            <section className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
              <div className="flex items-center gap-2 mb-2">
                <HeartPulse className="w-4 h-4 text-purple-300" />
                <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
                  Reported Conditions
                </span>
              </div>
              <ul className="space-y-1.5">
                {conditions.map((c, i) => (
                  <li key={i} className="text-sm text-slate-100 flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400 shrink-0" />
                    <span>{surveyConditionLabel(conditionLabel(c))}</span>
                    {conditionDetail(c) && (
                      <span className="text-xs text-slate-400">· {conditionDetail(c)}</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Consent */}
          {(submission.consentPdfUrl || submission.printedName) && (
            <section className="flex items-center gap-3 flex-wrap">
              {submission.consentPdfUrl && (
                <a
                  href={submission.consentPdfUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-sky-300 hover:bg-white/[0.08] transition"
                >
                  <FileText className="w-4 h-4" />
                  View signed consent PDF
                </a>
              )}
              {submission.printedName && (
                <span className="text-xs text-slate-400">
                  Signed as: <span className="text-slate-200">{submission.printedName}</span>
                </span>
              )}
            </section>
          )}

          {/* Clinical data (for referral) + Move action */}
          {alreadyMoved ? (
            <section className="rounded-xl border border-emerald-400/30 bg-emerald-500/10 p-3">
              <div className="flex items-center gap-2 text-sm text-emerald-200">
                <CheckCircle2 className="w-4 h-4" />
                Sent to Lung Center Referrals
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                {submission.movedToReferralsBy ? `By ${submission.movedToReferralsBy} ` : ""}
                {submission.movedToReferralsAt ? `· ${fmtTime(submission.movedToReferralsAt)}` : ""}
              </div>
              <div className="text-[11px] text-slate-400">
                This patient now exists in the consents collection under
                "Lung Center Referrals". Edit clinical details there.
              </div>
            </section>
          ) : (
            <>
              {showRespiratory || showSleep ? (
                <>
                  {/* Asthma — diagnosis-specific clinical data */}
                  {dx.includes("Asthma") && (
                    <ClinicalSection title="Asthma Clinical Data">
                      {renderEos()}
                      {renderExac()}
                      {renderAge()}
                      {renderPft()}
                      {renderFeno()}
                      {renderMeth()}
                      {renderCancer()}
                    </ClinicalSection>
                  )}

                  {/* COPD — diagnosis-specific clinical data */}
                  {dx.includes("COPD") && (
                    <ClinicalSection title="COPD Clinical Data">
                      {renderEos()}
                      {renderExac()}
                      {renderAge()}
                      {renderPft()}
                      {renderFeno()}
                      {renderOxygen()}
                      {renderCancer()}
                    </ClinicalSection>
                  )}

                  {/* Bronchiectasis — diagnosis-specific clinical data */}
                  {dx.includes("Bronchiectasis") && (
                    <ClinicalSection title="Bronchiectasis Clinical Data">
                      {renderEos()}
                      {renderExac()}
                      {renderAge()}
                      {renderPft()}
                      {renderFeno()}
                      {renderCancer()}
                    </ClinicalSection>
                  )}

                  {/* Sleep Apnea — diagnosis-specific clinical data */}
                  {dx.includes("Sleep Apnea") && (
                    <ClinicalSection title="Sleep Apnea Clinical Data">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className={LABEL_CLS}>BMI</span>
                          <input type="number" className={INPUT_CLS} value={clinical.bmi} onChange={(e) => setC("bmi", e.target.value)} />
                        </div>
                        <div>
                          <span className={LABEL_CLS}>AHI</span>
                          <input type="number" className={INPUT_CLS} value={clinical.ahi} onChange={(e) => setC("ahi", e.target.value)} />
                        </div>
                      </div>
                      {renderAge(false)}
                      <div>
                        <span className={LABEL_CLS}>A1C</span>
                        <div className="grid grid-cols-2 gap-2">
                          <input type="number" className={INPUT_CLS} value={clinical.a1c} onChange={(e) => setC("a1c", e.target.value)} />
                          <input type="date" className={INPUT_CLS + " [color-scheme:dark]"} value={clinical.a1cDate} onChange={(e) => setC("a1cDate", e.target.value)} />
                        </div>
                      </div>
                      <div>
                        <span className={LABEL_CLS}>Diabetes</span>
                        <select className={SELECT_CLS} value={clinical.diabetes} onChange={(e) => setC("diabetes", e.target.value)}>
                          <option value="">Select</option>
                          <option>Yes</option>
                          <option>No</option>
                        </select>
                      </div>
                      {renderCancer()}
                    </ClinicalSection>
                  )}
                </>
              ) : (
                <p className="text-[11px] text-slate-500">
                  Add a diagnosis (Asthma, COPD, or Sleep Apnea) via Edit to capture
                  diagnosis-specific clinical data before moving to Referrals.
                </p>
              )}

              {/* Move to Referrals */}
              <section className="rounded-xl border border-purple-300/30 bg-purple-500/10 p-3 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs text-slate-300 max-w-sm">
                  Copies this patient into{" "}
                  <strong className="text-slate-100">Lung Center Referrals</strong>{" "}
                  (consents) with the mapped details, clinical data, notes, and
                  audit trail. The Raw Database entry stays here under "Recently
                  Sent Patients".
                </div>
                {moveButton(false)}
              </section>
            </>
          )}

          {/* Notes */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <StickyNote className="w-4 h-4 text-purple-300" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
                Notes
              </span>
              <span className="ml-auto text-[10px] font-mono text-slate-400">{notes.length}</span>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.025] p-3">
              <textarea
                rows={3}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Log what happened on this call…"
                className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition resize-y min-h-[72px]"
                disabled={savingNote}
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={handleAddNote}
                  disabled={savingNote || !noteDraft.trim()}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(16,185,129,0.6)] active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingNote ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {savingNote ? "Saving…" : "Save Note"}
                </button>
              </div>
            </div>

            {notes.length > 0 && (
              <ul className="mt-2 space-y-2">
                {notes.map((n) => (
                  <li key={n.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                    <p className="text-sm text-slate-100 whitespace-pre-wrap leading-relaxed">{n.body}</p>
                    <div className="mt-1.5 flex items-center gap-2 text-[10px] text-slate-400">
                      <User className="w-2.5 h-2.5" />
                      <span className="truncate max-w-[180px]">{n.createdByEmail}</span>
                      <Clock className="w-2.5 h-2.5 ml-1" />
                      {fmtTime(n.createdAt)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {/* Audit trail */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <History className="w-4 h-4 text-purple-300" />
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-300">
                Audit Trail
              </span>
            </div>
            <ul className="space-y-1.5">
              {audit.map((ev) => (
                <li key={ev.id} className="flex items-start gap-2 text-xs text-slate-300">
                  <Activity className="w-3 h-3 text-purple-300 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <span className="text-slate-200">{ev.detail}</span>
                    <div className="text-[10px] text-slate-500">
                      {ev.actor} · {fmtTime(ev.at)}
                    </div>
                  </div>
                </li>
              ))}
              {/* Synthetic submission event — always the origin of the record. */}
              <li className="flex items-start gap-2 text-xs text-slate-300">
                <Activity className="w-3 h-3 text-emerald-300 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <span className="text-slate-200">
                    Form submitted via {submission.source || "referral link"}
                  </span>
                  <div className="text-[10px] text-slate-500">
                    {fmtTime(submission.submittedAt)}
                    {submission.ipAddress ? ` · IP ${submission.ipAddress}` : ""}
                  </div>
                </div>
              </li>
            </ul>
          </section>
        </div>
      </div>
    </div>
    {duplicateWarningDialog()}
    {/* Name + DOB advisory, shown only when the MRN check found nothing. The
        move it guards is unchanged: "Add Anyway" runs the same confirm and the
        same performMoveToReferrals() as before this check existed. */}
    <PotentialDuplicateDialog
      matches={nameDobMatches}
      entered={{
        name: submission.name,
        dob: submission.dob,
        mrn: normalizeMrn9(submission.mrn),
      }}
      busy={moving}
      onCancel={() => setNameDobMatches([])}
      onProceed={() => {
        if (
          !window.confirm(
            "Copy this patient into Lung Center Referrals anyway?\n\nA possible duplicate was found with the same date of birth."
          )
        )
          return;
        setNameDobMatches([]);
        performMoveToReferrals();
      }}
    />
    </>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
// Cards per page, per section (New / Moved to Referrals / Moved to
// Duplicates) — each section paginates independently so the "New Submissions"
// call-duty queue never gets pushed off-page by an active Referrals backlog.
const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { key: "newest", label: "Newest submitted" },
  { key: "oldest", label: "Oldest submitted" },
  { key: "noted", label: "Recently noted" },
];

const ORIGIN_FILTERS = [
  { key: "all", label: "All Origins" },
  { key: "regx", label: "RegX" },
  { key: "lungx", label: "LungX" },
];

// "New" here doesn't collide with MOVE_TAGS' "new" key — this is the raw
// origin string on the submission, not where it's been moved to.
const originKeyOf = (s) =>
  String(s?.origin || "").toLowerCase().startsWith("lungx") ? "lungx" : "regx";

// A submission's diagnosis lives in one of two places: `diagnosis` (staff-only,
// set via the Edit modal's DISEASE_OPTIONS pills — absent on any submission
// nobody has reviewed yet) or `conditions` (what the PATIENT actually picked on
// the questionnaire, via ReferralQuestionnairePage's own ALL_CONDITIONS list —
// present the moment they submit). Filtering by diagnosis has to check both, or
// every brand-new submission is invisible to it. The two option lists mostly
// share exact strings ("COPD", "Asthma", …) except IPF, which the questionnaire
// calls "Pulmonary Fibrosis" (shown to staff as ILD, stored as IPF). Keys are
// STORED option values; entries are the questionnaire's stored condition names.
const DIAGNOSIS_SYNONYMS = {
  IPF: ["pulmonary fibrosis"],
  Sarcoid: ["sarcoidosis"],
  "Heart Disease": ["heart disease"],
  Cancer: ["cancer (other)", "lung cancer"],
};

const submissionHasDiagnosis = (s, option) => {
  const optionLower = option.toLowerCase();
  const variants = [optionLower, ...(DIAGNOSIS_SYNONYMS[option] || [])];
  const dx = Array.isArray(s.diagnosis) ? s.diagnosis : [];
  if (dx.some((d) => String(d).toLowerCase() === optionLower)) return true;
  const conds = Array.isArray(s.conditions) ? s.conditions : [];
  return conds.some((c) => variants.includes(conditionLabel(c).toLowerCase()));
};

// Local calendar day (not UTC) for the "on this exact date" filter, so a
// submission at 11pm local time isn't attributed to the next day.
const localDateKey = (ts) => {
  const ms = millis(ts);
  if (!ms) return null;
  const d = new Date(ms);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// `lastNoteAt` is stamped on the submission doc itself the moment a note is
// added (see handleAddNote above), so it's already in the initial snapshot —
// no query needed. `latestNotes[id]` (the per-submission fetch used for card
// previews) is only a fallback, for submissions noted before that field
// existed and not yet covered by the one-off backfill script.
const noteTimeOf = (s, latestNotes) => {
  if (s.lastNoteAt) return millis(s.lastNoteAt);
  return latestNotes[s.id]?.createdAt ? millis(latestNotes[s.id].createdAt) : -1;
};

const sortSubmissions = (rows, sortBy, latestNotes) => {
  const copy = [...rows];
  if (sortBy === "oldest") {
    copy.sort((a, b) => millis(a.submittedAt) - millis(b.submittedAt));
  } else if (sortBy === "noted") {
    // Most-recently-noted first; submissions with no note sink to the bottom
    // (in their existing newest-submitted order).
    copy.sort((a, b) => {
      const an = noteTimeOf(a, latestNotes);
      const bn = noteTimeOf(b, latestNotes);
      if (an !== bn) return bn - an;
      return millis(b.submittedAt) - millis(a.submittedAt);
    });
  } else {
    copy.sort((a, b) => millis(b.submittedAt) - millis(a.submittedAt));
  }
  return copy;
};

// "Showing X–Y of Z" + prev/next, reused for each of the 3 sections below.
const PaginationBar = ({ page, setPage, total }) => {
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(page * PAGE_SIZE, total);
  if (total <= PAGE_SIZE) {
    return (
      <div className="mt-2 text-[11px] text-slate-500">
        Showing {start}–{end} of {total}
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
      <span className="text-[11px] text-slate-500">
        Showing {start}–{end} of {total}
      </span>
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          disabled={page <= 1}
          className="px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] text-slate-300 hover:bg-white/[0.08] transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Prev
        </button>
        <span className="text-[11px] text-slate-400 px-1">
          Page {page} of {totalPages}
        </span>
        <button
          onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          disabled={page >= totalPages}
          className="px-2.5 py-1 rounded-lg bg-white/[0.04] border border-white/10 text-[11px] text-slate-300 hover:bg-white/[0.08] transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );
};

// Multi-select diagnosis filter — a popover of checkboxes over DISEASE_OPTIONS.
// A submission matches when it has ANY of the selected diagnoses (empty
// selection = no filter).
const DiagnosisFilter = ({ selected, setSelected }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const toggle = (d) =>
    setSelected((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]
    );

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`text-xs px-3 py-1.5 rounded-full border transition inline-flex items-center gap-1.5 ${
          selected.length > 0
            ? "bg-purple-500/20 border-purple-300/40 text-purple-100"
            : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]"
        }`}
      >
        <HeartPulse className="w-3 h-3" />
        Diagnosis{selected.length > 0 ? ` (${selected.length})` : ""}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="diagnosis-dropdown-menu absolute z-20 mt-1.5 w-56 rounded-xl border border-white/10 bg-slate-950/95 backdrop-blur-2xl shadow-[0_20px_50px_-15px_rgba(0,0,0,0.7)] p-2 max-h-64 overflow-y-auto">
          {selected.length > 0 && (
            <button
              onClick={() => setSelected([])}
              className="w-full text-left text-[11px] text-purple-300 hover:text-white px-2 py-1 mb-1"
            >
              Clear all
            </button>
          )}
          {DISEASE_OPTIONS.map((d) => (
            <label
              key={d}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white/[0.05] cursor-pointer text-xs text-slate-200"
            >
              <input type="checkbox" checked={selected.includes(d)} onChange={() => toggle(d)} />
              {diagnosisLabel(d)}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const RawDatabasePage = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [originFilter, setOriginFilter] = useState("all");
  const [diagnosisFilter, setDiagnosisFilter] = useState([]);
  const [onDate, setOnDate] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [bulkMoving, setBulkMoving] = useState(false);
  // submissionId -> the newest note on it, or null for "looked, there are none".
  // Both are answers; only an id ABSENT from this map is still unknown, which is
  // what stops the fetch below re-asking about patients with no notes.
  const [latestNotes, setLatestNotes] = useState({});

  // Independent page cursors per section — see PAGE_SIZE above.
  const [pageNew, setPageNew] = useState(1);
  const [pageSent, setPageSent] = useState(1);
  const [pageDup, setPageDup] = useState(1);

  useEffect(() => {
    // Site isolation: referral docs all live under one pinned path but carry the
    // siteId they belong to, so scope the list to the ACTIVE site — otherwise one
    // site's staff would see every site's referrals (they all shared this bucket).
    const unsub = onSnapshot(
      query(
        collection(db, getReferralSubmissionsPath()),
        where("siteId", "==", getActiveSiteId())
      ),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => millis(b.submittedAt) - millis(a.submittedAt));
        setSubmissions(rows);
        setLoading(false);
      },
      (err) => {
        console.error("Referral submissions subscribe failed:", err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Newest note per patient, for the card previews.
  //
  // One small query per submission (newest note, limit 1), because a note lives
  // in that submission's own `notes` subcollection and the rules match it
  // nested there — which does not authorize a collection-group read, and
  // `patient_notes/{id}/notes` shares the collection name anyway. Run in small
  // batches so opening the page doesn't fire a few hundred requests at once,
  // and asked once per id: the answer is cached whether or not a note was
  // found, so filtering and re-rendering re-read nothing.
  //
  // Deliberately NOT a live subscription per patient — that would be one open
  // listener per card. The one patient whose notes can change while you look at
  // them is the one you have open, and the modal already streams those back
  // through onLatestNote.
  const noteFetchState = useRef({ asked: new Set(), running: false });
  useEffect(() => {
    const state = noteFetchState.current;
    const pending = submissions
      .map((s) => s.id)
      .filter((id) => id && !state.asked.has(id));
    if (!pending.length || state.running) return;

    let cancelled = false;
    state.running = true;
    pending.forEach((id) => state.asked.add(id));

    (async () => {
      const BATCH = 12;
      try {
        for (let i = 0; i < pending.length; i += BATCH) {
          if (cancelled) return;
          const slice = pending.slice(i, i + BATCH);
          const found = await Promise.all(
            slice.map(async (id) => {
              try {
                const snap = await getDocs(
                  query(
                    collection(db, getReferralSubmissionNotesPath(id)),
                    orderBy("createdAt", "desc"),
                    limit(1)
                  )
                );
                const d = snap.docs[0];
                return [id, d ? { id: d.id, ...d.data() } : null];
              } catch (err) {
                // Fail-soft, per patient: a preview is a convenience, and one
                // unreadable subcollection must not cost the whole page its
                // previews. `null` reads as "no note" on the card.
                console.error("Latest note read failed:", id, err);
                return [id, null];
              }
            })
          );
          if (cancelled) return;
          setLatestNotes((prev) => ({ ...prev, ...Object.fromEntries(found) }));
        }
      } finally {
        state.running = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [submissions]);

  // Called by the open modal on every notes snapshot, so the card behind it
  // stays current. Stable, or it would re-subscribe the modal on every render.
  const handleLatestNote = useCallback((id, note) => {
    noteFetchState.current.asked.add(id);
    setLatestNotes((prev) => ({ ...prev, [id]: note || null }));
  }, []);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const rows = submissions.filter((s) => {
      if (statusFilter !== "all" && moveStatusOf(s).key !== statusFilter)
        return false;
      if (originFilter !== "all" && originKeyOf(s) !== originFilter) return false;
      if (diagnosisFilter.length > 0 && !diagnosisFilter.some((opt) => submissionHasDiagnosis(s, opt)))
        return false;
      if (onDate && localDateKey(s.submittedAt) !== onDate) return false;
      if (!term) return true;
      return (
        String(s.name || "").toLowerCase().includes(term) ||
        String(s.phone || "").toLowerCase().includes(term) ||
        String(s.email || "").toLowerCase().includes(term)
      );
    });
    return sortSubmissions(rows, sortBy, latestNotes);
  }, [submissions, search, statusFilter, originFilter, diagnosisFilter, onDate, sortBy, latestNotes]);

  // True while some visible submission's note-recency is genuinely unknown —
  // neither the denormalized `lastNoteAt` nor the per-submission preview
  // fetch has an answer for it yet. Only relevant to the "noted" sort; drives
  // the spinner next to the sort dropdown below.
  const notesCatchingUp = useMemo(
    () => filtered.some((s) => !s.lastNoteAt && latestNotes[s.id] === undefined),
    [filtered, latestNotes]
  );

  // Any filter/sort change invalidates the current page position in every
  // section — back to page 1 rather than showing an out-of-range slice.
  useEffect(() => {
    setPageNew(1);
    setPageSent(1);
    setPageDup(1);
  }, [search, statusFilter, originFilter, diagnosisFilter, onDate, sortBy]);

  const selected = submissions.find((s) => s.id === selectedId) || null;

  // Sent patients are COPIED (not removed) — keep them, just in their own
  // "Recently Sent Patients" section.
  const activeFiltered = filtered.filter(
    (s) => !s.movedToReferrals && !s.movedToDuplicates
  );
  const sentFiltered = filtered.filter((s) => s.movedToReferrals);
  const duplicatesMovedFiltered = filtered.filter(
    (s) => s.movedToDuplicates && !s.movedToReferrals
  );

  const pagedNew = activeFiltered.slice((pageNew - 1) * PAGE_SIZE, pageNew * PAGE_SIZE);
  const pagedSent = sentFiltered.slice((pageSent - 1) * PAGE_SIZE, pageSent * PAGE_SIZE);
  const pagedDup = duplicatesMovedFiltered.slice((pageDup - 1) * PAGE_SIZE, pageDup * PAGE_SIZE);

  // Bulk version of SubmissionModal's "Move to Referrals" — copies every
  // currently-visible "New" submission into consents with no clinical data
  // filled in (there's no per-patient form in a one-click bulk action);
  // coordinators fill that in later from the patient record. Notes aren't
  // copied since New submissions haven't been reviewed yet.
  const handleMoveAllNewToReferrals = async () => {
    const newOnes = activeFiltered;
    if (newOnes.length === 0 || bulkMoving) return;
    if (
      !window.confirm(
        `Move all ${newOnes.length} new patient${newOnes.length === 1 ? "" : "s"} to Referrals?\n\nThis copies each into Lung Center Referrals (consents) with no clinical data filled in. The Raw Database entries are NOT removed — they move to "Moved to Referrals".`
      )
    )
      return;

    setBulkMoving(true);
    const actor = auth.currentUser?.email || "unknown";
    const skipped = [];

    // Name + DOB advisory for the bulk run. Unlike the single-patient path there
    // is no dialog to stop on, so this does NOT gate the move. Every patient
    // still moves exactly as before. Matches are collected and reported in one
    // summary at the end so staff know which records to go merge.
    //
    // Loaded ONCE for the whole run (the per-patient path re-reads each time,
    // which would be one full-collection read per patient here). Fail-soft: if
    // the read fails the advisory is simply skipped, never the move.
    let dedupCandidates = null;
    try {
      dedupCandidates = await loadDedupCandidates();
    } catch (e) {
      console.error("Name/DOB dedup candidates unavailable for bulk move:", e);
    }
    const flagged = [];

    for (const s of newOnes) {
      try {
        const existing = await findExistingPatientByMrn(s.mrn);
        if (existing) {
          skipped.push({
            name: s.name || "Unknown patient",
            mrn: normalizeMrn9(s.mrn),
            label: existing.label,
          });
          continue;
        }

        // MRN is clean, so note (without blocking) anyone who looks like the same
        // person under a different MRN.
        if (dedupCandidates) {
          const hits = findPotentialDuplicates(
            { name: s.name, dob: s.dob, mrn: s.mrn },
            dedupCandidates
          );
          if (hits.length) {
            flagged.push({
              name: s.name || "Unknown patient",
              mrn: normalizeMrn9(s.mrn),
              hits,
            });
          }
        }

        const dxArr = Array.isArray(s.diagnosis) ? s.diagnosis : [];
        const payload = {
          name: s.name || "",
          dob: s.dob || "",
          phone: s.phone || "",
          email: s.email || "",
          physicalAddress: s.address || "",
          diagnosis: dxArr,
          status: "Lung Center Referrals",
          notes: buildLegacyNotes(s),
          consentUrl: s.consentPdfUrl || "",
          mrn: normalizeMrn9(s.mrn),
          study: "",
          potentialStudyIds: [],
          studyEligibility: {},
          emergencyContactName: "",
          emergencyContactPhone: "",
          callMade: false,
          scheduled: false,
          dateScheduled: "",
          rescreenDate: "",
          urgent: false,
          urgentReason: "",
          ...initClinical(s),
          source: "ReferralRawDatabase",
          referralSubmissionId: s.id,
          referralToken: s.token || "",
          signedAt: serverTimestamp(),
          createdAt: serverTimestamp(),
          movedToReferralsBy: actor,
          movedToReferralsAt: serverTimestamp(),
        };

        const consRef = await addDoc(collection(db, getConsentsPath()), payload);
        const newId = consRef.id;

        // Fold the record we just created into the candidate list so two
        // duplicates of each other INSIDE this same batch are caught. The
        // snapshot above was taken before any of them existed.
        if (dedupCandidates) {
          dedupCandidates.push({
            id: newId,
            name: payload.name,
            dob: payload.dob,
            mrn: payload.mrn,
            sourceLabel: "Referrals (this batch)",
          });
        }

        await logPatientAuditEvent({
          patientId: newId,
          title: `Referral form submitted via ${s.source || "referral link"}`,
          action: "REFERRAL_FORM_SUBMITTED",
          patientName: s.name || "",
          actorEmail: "patient",
          metadata: {
            submittedAt: fmtTime(s.submittedAt),
            ipAddress: s.ipAddress || "",
          },
        });
        await logPatientAuditEvent({
          patientId: newId,
          title: "Moved to Lung Center Referrals from Raw Database (bulk action)",
          action: "REFERRAL_MOVED_TO_CONSENTS",
          patientName: s.name || "",
          actorEmail: actor,
          metadata: { referralSubmissionId: s.id },
        });

        await updateDoc(doc(db, getReferralSubmissionsPath(), s.id), {
          movedToReferrals: true,
          movedToReferralsAt: serverTimestamp(),
          movedToReferralsBy: actor,
          consentDocId: newId,
        });
        await addDoc(collection(db, getReferralSubmissionAuditPath(s.id)), {
          type: "move",
          detail: `Moved to Lung Center Referrals (consents/${newId}) via bulk action`,
          actor,
          at: serverTimestamp(),
        });
      } catch (err) {
        console.error(`Bulk move to Referrals failed for submission ${s.id}:`, err);
      }
    }
    setBulkMoving(false);

    if (skipped.length > 0) {
      const lines = skipped.map(
        (p) => `• ${p.name} (MRN ${p.mrn}) — already exists in ${p.label}`
      );
      window.alert(
        `${skipped.length} patient${skipped.length === 1 ? "" : "s"} skipped because a matching MRN already exists:\n\n${lines.join(
          "\n"
        )}`
      );
    }

    // Advisory, shown after the skipped report so the blocking result is read
    // first. These patients WERE moved (the MRN was new), but each one looks
    // like somebody already in the portal, which usually means an MRN was
    // mistyped somewhere. Named separately so nobody reads it as a failure.
    if (flagged.length > 0) {
      const blocks = flagged.map((p) => {
        const matchLines = p.hits
          .slice(0, 3)
          .map(
            (h) =>
              `    - ${h.patient.fullName || h.patient.name || "Unknown name"}` +
              ` (DOB ${h.patient.dob || "not given"}, MRN ${h.patient.mrn || "not given"})` +
              ` in ${h.patient.sourceLabel || "the portal"}`
          );
        if (p.hits.length > 3) {
          matchLines.push(`    - …and ${p.hits.length - 3} more`);
        }
        return `• ${p.name} (MRN ${p.mrn}) may already exist as:\n${matchLines.join("\n")}`;
      });
      window.alert(
        `Heads up: ${flagged.length} patient${
          flagged.length === 1 ? " was" : "s were"
        } moved, but ${
          flagged.length === 1 ? "it" : "they"
        } may already be in the portal under a different MRN.\n\n` +
          `Nothing was blocked. Please recheck the MRNs below and merge if needed:\n\n${blocks.join(
            "\n\n"
          )}`
      );
    }
  };

  const renderCard = (s) => {
    const meta = moveStatusOf(s);
    const origin = originOf(s);
    const moved = meta.key !== "new";
    const condCount = Array.isArray(s.conditions) ? s.conditions.length : 0;
    const dxCount = Array.isArray(s.diagnosis) ? s.diagnosis.length : 0;
    // First line of the newest note. A note's later lines are its detail; the
    // first one is what someone wrote it to say, and it is all a card has room
    // for. `undefined` here means the fetch hasn't answered yet, which draws
    // exactly as "no note" — no spinner on a card for something this small.
    const noteLine = firstLineOf(latestNotes[s.id]?.body);
    return (
      <button
        key={s.id}
        onClick={() => setSelectedId(s.id)}
        className={`text-left rounded-2xl border p-4 transition group backdrop-blur-xl ${
          meta.key === "referrals"
            ? "border-emerald-400/20 bg-emerald-500/[0.06] hover:bg-emerald-500/10"
            : meta.key === "duplicates"
            ? "border-rose-400/20 bg-rose-500/[0.06] hover:bg-rose-500/10"
            : "border-white/10 bg-white/[0.04] hover:border-purple-300/30 hover:bg-white/[0.06]"
        } ${
          // Amber ring for "somebody has written on this one". Layered OVER the
          // status colour rather than replacing it: whether a patient has moved
          // to Referrals or Duplicates is what the card's colour has always
          // meant, and a note is a second, independent fact. Amber is free —
          // emerald, rose and purple are all spoken for above.
          noteLine ? "note-flagged ring-1 ring-amber-300/40 border-amber-300/30" : ""
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-semibold text-white truncate group-hover:text-purple-100">
              {s.name || "Unknown patient"}
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
              <Phone className="w-3 h-3" />
              <span className="font-mono">{s.phone || "—"}</span>
            </div>
          </div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full border shrink-0 flex items-center gap-1 ${meta.badge}`}>
            {moved && <CheckCircle2 className="w-2.5 h-2.5" />}
            {meta.label}
          </span>
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] px-2 py-0.5 rounded-full border flex items-center gap-1 ${origin.badge}`}>
            {origin.label}
          </span>
          {dxCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-500/15 border border-purple-300/30 text-purple-200 flex items-center gap-1">
              <HeartPulse className="w-2.5 h-2.5" /> {dxCount} dx
            </span>
          )}
          {s.weightManagementInterest === "yes" && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-slate-300 flex items-center gap-1">
              <Weight className="w-2.5 h-2.5" /> Weight mgmt
            </span>
          )}
          {condCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-slate-300 flex items-center gap-1">
              <HeartPulse className="w-2.5 h-2.5" /> {condCount} reported
            </span>
          )}
          {s.consentPdfUrl && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.05] border border-white/10 text-slate-300 flex items-center gap-1">
              <CheckCircle2 className="w-2.5 h-2.5 text-emerald-300" /> Signed
            </span>
          )}
        </div>

        {/* The newest note's first line, so the card says what was written
            without anyone opening it. Truncated to one line — the full note,
            and every earlier one, is in the modal as before. */}
        {noteLine && (
          <div className="note-preview mt-3 flex items-start gap-1.5 rounded-lg border border-amber-300/20 bg-amber-400/[0.07] px-2 py-1.5">
            <StickyNote className="w-3 h-3 shrink-0 mt-[1px] text-amber-300" />
            <span className="min-w-0 truncate text-[11px] text-amber-100/90">
              {noteLine}
            </span>
          </div>
        )}

        <div className="mt-3 text-[10px] text-slate-500 flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {meta.key === "referrals" && s.movedToReferralsAt
            ? `Moved ${fmtTime(s.movedToReferralsAt)}`
            : meta.key === "duplicates" && s.movedToDuplicatesAt
            ? `Moved ${fmtTime(s.movedToDuplicatesAt)}`
            : fmtTime(s.submittedAt)}
        </div>
      </button>
    );
  };

  return (
    // `raw-database-page` is the light-mode scope hook — see index.css. The
    // app-wide light rules match exact class tokens, which misses every
    // opacity-suffixed one (`text-slate-300/80`, `text-amber-100/90`) and
    // misses placeholders entirely; those stayed pale on a white page. Same
    // pattern the referral and sourcex pages already use.
    <div className="raw-database-page relative min-h-screen w-full text-slate-100 font-[Manrope]">
      <PageBackground />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header + back */}
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-purple-300 font-semibold">
                Referral · Call Duty
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-white">
                Raw Database
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/coordinator/raw-database/query")}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-slate-200 hover:bg-white/10 transition"
            >
              <Filter className="w-4 h-4 text-purple-300" />
              Query &amp; Export
            </button>
            <span className="text-xs text-slate-400">{submissions.length} total submissions</span>
          </div>
        </div>

        {/* Search + filter */}
        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, phone, or email"
              className="w-full pl-9 pr-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition"
            />
          </div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              onClick={() => setStatusFilter("all")}
              className={`text-xs px-3 py-1.5 rounded-full border transition ${
                statusFilter === "all"
                  ? "bg-purple-500/20 border-purple-300/40 text-purple-100"
                  : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]"
              }`}
            >
              All
            </button>
            {MOVE_TAGS.map((s) => (
              <button
                key={s.key}
                onClick={() => setStatusFilter(s.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  statusFilter === s.key
                    ? s.badge + " ring-1 ring-white/30"
                    : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        {/* Sort + origin + diagnosis filters */}
        <div className="flex items-center gap-2 mb-6 flex-wrap">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="text-xs px-3 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-slate-300 hover:bg-white/[0.07] focus:outline-none focus:border-purple-400/50 transition cursor-pointer"
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.key} value={o.key} className="bg-slate-900">
                Sort: {o.label}
              </option>
            ))}
          </select>
          {sortBy === "noted" && notesCatchingUp && (
            <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
              <Loader2 className="w-3 h-3 animate-spin" />
              Loading note order…
            </span>
          )}
          <div className="flex items-center gap-1.5 flex-wrap">
            {ORIGIN_FILTERS.map((o) => (
              <button
                key={o.key}
                onClick={() => setOriginFilter(o.key)}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${
                  originFilter === o.key
                    ? "bg-indigo-500/20 border-indigo-300/40 text-indigo-100"
                    : "bg-white/[0.03] border-white/10 text-slate-300 hover:bg-white/[0.07]"
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <DiagnosisFilter selected={diagnosisFilter} setSelected={setDiagnosisFilter} />
          <div className="relative">
            <Calendar className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-500 pointer-events-none" />
            <input
              type="date"
              value={onDate}
              onChange={(e) => setOnDate(e.target.value)}
              className="pl-7 pr-2 py-1.5 rounded-full bg-white/[0.03] border border-white/10 text-xs text-slate-300 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]"
            />
          </div>
          {onDate && (
            <button
              onClick={() => setOnDate("")}
              className="text-[11px] text-purple-300 hover:text-purple-200 transition"
            >
              Clear date
            </button>
          )}
        </div>

        {/* "How many showed up on this day" readout, across all statuses and
            respecting the other active filters. */}
        {onDate && (
          <div className="mb-4 text-xs text-slate-400">
            <span className="font-semibold text-purple-200">{filtered.length}</span>{" "}
            submission{filtered.length === 1 ? "" : "s"} on{" "}
            {new Date(onDate + "T00:00:00").toLocaleDateString("en-US", {
              month: "long",
              day: "numeric",
              year: "numeric",
            })}
          </div>
        )}

        {/* Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            Loading submissions…
          </div>
        ) : activeFiltered.length === 0 &&
          sentFiltered.length === 0 &&
          duplicatesMovedFiltered.length === 0 ? (
          <div className="text-center py-20 text-slate-500">
            No submissions match your filters.
          </div>
        ) : (
          <>
            {activeFiltered.length > 0 && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-300">
                    New Submissions
                  </span>
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-xs text-slate-400">{activeFiltered.length}</span>
                  <button
                    onClick={handleMoveAllNewToReferrals}
                    disabled={bulkMoving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 ring-1 ring-white/20 text-xs font-semibold text-white hover:shadow-[0_8px_24px_-6px_rgba(139,92,246,0.6)] active:scale-[0.98] transition disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {bulkMoving ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <ArrowRightCircle className="w-3.5 h-3.5" />
                    )}
                    {bulkMoving ? "Moving…" : "Move All to Referrals"}
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pagedNew.map((s) => renderCard(s))}
                </div>
                <PaginationBar page={pageNew} setPage={setPageNew} total={activeFiltered.length} />
              </div>
            )}

            {/* Recently Sent Patients — copied into Lung Center Referrals */}
            {sentFiltered.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <Send className="w-4 h-4 text-emerald-300" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-300">
                    Moved to Referrals
                  </span>
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-xs text-slate-400">{sentFiltered.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pagedSent.map((s) => renderCard(s))}
                </div>
                <PaginationBar page={pageSent} setPage={setPageSent} total={sentFiltered.length} />
              </div>
            )}

            {/* Moved to Duplicates — copied into the duplicates collection */}
            {duplicatesMovedFiltered.length > 0 && (
              <div className="mt-8">
                <div className="flex items-center gap-2 mb-3">
                  <ArrowRightCircle className="w-4 h-4 text-rose-300" />
                  <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-slate-300">
                    Moved to Duplicates
                  </span>
                  <span className="h-px flex-1 bg-white/10" />
                  <span className="text-xs text-slate-400">{duplicatesMovedFiltered.length}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {pagedDup.map((s) => renderCard(s))}
                </div>
                <PaginationBar page={pageDup} setPage={setPageDup} total={duplicatesMovedFiltered.length} />
              </div>
            )}
          </>
        )}
      </div>

      {selected && (
        <SubmissionModal
          key={selected.id}
          submission={selected}
          onClose={() => setSelectedId(null)}
          onLatestNote={handleLatestNote}
        />
      )}
    </div>
  );
};

export default RawDatabasePage;
